import { Ionicons } from '@expo/vector-icons';
import { Image } from 'expo-image';
import { useLocalSearchParams, useRouter } from 'expo-router';
import React, { useState } from 'react';
import { ScrollView, View } from 'react-native';
import { useTranslation } from 'react-i18next';

import { AppText } from '@/components/ui/AppText';
import { BackHeader } from '@/components/ui/BackHeader';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { EmptyState } from '@/components/ui/EmptyState';
import { Screen } from '@/components/ui/Screen';
import { ShareButton } from '@/components/ui/ShareButton';
import { Skeleton } from '@/components/ui/Skeleton';
import { useToast } from '@/components/ui/Toast';
import { useThemePalette } from '@/config/theme';
import { useFriendLeagueBoard, useLeaveFriendLeague, useMyFriendLeagues } from '@/features/games/friendLeagues';
import { buildLeagueInviteLink } from '@/lib/deeplink';
import type { FriendLeagueBoardEntry } from '@/lib/data/provider';
import { formatNumber } from '@/lib/i18n/format';
import { useLocale } from '@/lib/i18n/LocaleProvider';
import { errorKey } from '@/lib/utils/errors';

const PODIUM = ['#F4C84B', '#C7CDDA', '#CD8B53'];
const APP_LINK = 'https://play.google.com/store/apps/details?id=com.tashkeela.app';

function Avatar({ uri, name, size = 32 }: { uri: string | null; name: string; size?: number }) {
  if (uri) {
    return <Image source={{ uri }} style={{ width: size, height: size, borderRadius: size / 2 }} contentFit="cover" cachePolicy="memory-disk" />;
  }
  return (
    <View
      style={{ width: size, height: size, borderRadius: size / 2 }}
      className="items-center justify-center bg-inset"
    >
      <AppText weight="bold" className="text-volt-bright" style={{ fontSize: size * 0.42 }}>
        {(name || '?').charAt(0).toUpperCase()}
      </AppText>
    </View>
  );
}

function BoardRow({ e }: { e: FriendLeagueBoardEntry }) {
  const palette = useThemePalette();
  const { numeralOptions } = useLocale();
  const medal = e.rank <= 3 ? PODIUM[e.rank - 1] : null;
  return (
    <View className={`flex-row items-center gap-3 px-4 py-3 ${e.is_me ? 'bg-volt/10' : ''}`}>
      {medal ? (
        <View className="h-6 w-6 items-center justify-center rounded-full" style={{ backgroundColor: medal }}>
          <AppText weight="bold" tabular style={{ color: '#1A1408', fontSize: 11 }}>
            {formatNumber(e.rank, numeralOptions)}
          </AppText>
        </View>
      ) : (
        <AppText variant="caption" weight="bold" tabular className="w-6 text-center text-muted">
          {formatNumber(e.rank, numeralOptions)}
        </AppText>
      )}
      <Avatar uri={e.avatar_url} name={e.display_name ?? 'Player'} />
      <AppText
        variant="caption"
        weight={e.is_me ? 'bold' : 'medium'}
        className="flex-1 text-ink"
        numberOfLines={1}
      >
        {e.display_name ?? 'Player'}
      </AppText>
      {e.is_me ? <Ionicons name="person" size={12} color={palette.voltBright} /> : null}
      <AppText variant="caption" weight="bold" tabular className="text-volt-bright">
        {formatNumber(e.score, numeralOptions)}
      </AppText>
    </View>
  );
}

export default function FriendLeagueScreen() {
  const palette = useThemePalette();
  const router = useRouter();
  const { id } = useLocalSearchParams<{ id: string }>();
  const { t } = useTranslation();
  const { numeralOptions } = useLocale();
  const toast = useToast();

  const leagues = useMyFriendLeagues();
  const league = (leagues.data ?? []).find((l) => l.id === id);
  const board = useFriendLeagueBoard(id);
  const leave = useLeaveFriendLeague();
  const [confirmLeave, setConfirmLeave] = useState(false);

  const entries = board.data ?? [];
  const isDuel = league?.is_duel ?? false;
  const duelReady = isDuel && entries.length === 2;

  // Share the 1-tap deep link (opens the app straight to the pre-filled join),
  // plus the store link as a fallback for anyone who hasn't installed yet.
  const inviteMessage = league
    ? `${t('friendLeagues.shareInvite', { name: league.name, code: league.invite_code })} ${buildLeagueInviteLink(league.invite_code)} ${APP_LINK}`
    : '';

  const onLeave = () => {
    if (!confirmLeave) {
      setConfirmLeave(true);
      setTimeout(() => setConfirmLeave(false), 5000);
      return;
    }
    if (id) {
      leave.mutate(id, {
        onSuccess: () => router.back(),
        onError: (e) => toast.show({ message: t(errorKey(e)), tone: 'error' }),
      });
    }
  };

  return (
    <Screen>
      <BackHeader
        title={league?.name ?? t('friendLeagues.title')}
        right={
          league ? <ShareButton message={inviteMessage} accessibilityLabel={t('common.share')} /> : undefined
        }
      />
      <ScrollView contentContainerStyle={{ paddingBottom: 40, gap: 12 }}>
        {/* Invite code */}
        {league ? (
          <Card className="mx-4 mt-1 flex-row items-center justify-between px-4 py-3">
            <View>
              <AppText variant="micro" className="uppercase tracking-wide text-muted">
                {t('friendLeagues.inviteCode')}
              </AppText>
              <AppText variant="title" weight="bold" tabular className="tracking-[3px] text-volt-bright">
                {league.invite_code}
              </AppText>
            </View>
            <View className="flex-row items-center gap-1.5">
              <Ionicons name={isDuel ? 'flash' : 'people'} size={16} color={palette.soft} />
              <AppText variant="caption" className="text-soft">
                {isDuel
                  ? t('friendLeagues.duelTag')
                  : t('friendLeagues.memberCount', {
                      n: formatNumber(league.member_count, numeralOptions),
                    })}
              </AppText>
            </View>
          </Card>
        ) : null}

        {/* 1v1 head-to-head banner */}
        {duelReady ? (
          <Card className="mx-4 flex-row items-center justify-between px-4 py-4">
            {[entries[0], entries[1]].map((p, i) => (
              <React.Fragment key={p.user_id}>
                <View className={`flex-1 items-center gap-1.5 ${i === 1 ? 'items-center' : ''}`}>
                  <Avatar uri={p.avatar_url} name={p.display_name ?? 'Player'} size={48} />
                  <AppText variant="micro" weight="semibold" className="text-ink" numberOfLines={1}>
                    {p.display_name ?? 'Player'}
                  </AppText>
                  <AppText variant="title" weight="bold" tabular className="text-volt-bright">
                    {formatNumber(p.score, numeralOptions)}
                  </AppText>
                </View>
                {i === 0 ? (
                  <AppText variant="title" weight="bold" className="px-2 text-muted">
                    {t('friendLeagues.vs')}
                  </AppText>
                ) : null}
              </React.Fragment>
            ))}
          </Card>
        ) : null}

        {/* The cross-game ranking */}
        <AppText variant="caption" weight="bold" className="mx-4 mt-1 uppercase tracking-wide text-soft">
          {t('friendLeagues.standings')}
        </AppText>
        <AppText variant="micro" className="mx-4 -mt-1 text-muted">
          {t('friendLeagues.scoreNote')}
        </AppText>
        {board.isPending ? (
          <View className="mx-4 gap-2">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </View>
        ) : entries.length === 0 ? (
          <EmptyState icon="people-outline" title={t('friendLeagues.emptyBoard')} />
        ) : (
          <Card className="mx-4 overflow-hidden">
            {entries.map((e, i) => (
              <View key={e.user_id} className={i > 0 ? 'border-t border-line' : ''}>
                <BoardRow e={e} />
              </View>
            ))}
          </Card>
        )}

        {league ? (
          <View className="mx-4 mt-2">
            <Button
              label={confirmLeave ? t('friendLeagues.confirmLeave') : t('friendLeagues.leave')}
              variant="ghost"
              loading={leave.isPending}
              onPress={onLeave}
            />
          </View>
        ) : null}
      </ScrollView>
    </Screen>
  );
}
